export const CUSTOM_FIELD_KEYS = Array.from(
  { length: 20 },
  (_, i) => `c${i + 1}value`,
);

export interface UserWizardData {
  name: string;
  surname: string;
  external_id: string;
  email: string;
  username: string;
  roles: any[];
  admin_flag: boolean;
  external_employee_flag: boolean;
  new_hire_flag: boolean;
  manager_flag: boolean;

  phone_number: string;
  address: string;
  gender: string;
  fiscal_code: string;
  birth_country: string;
  birth_province: string;
  birthday: any;
  privacy_consent_flag: boolean;
  smart_working_flag: boolean;
  receieved_credentials_flag: boolean;
  branch: string;
  language: string;
  preference_language: string;
  ateco_code: string;
  country_code: string;
  country_description: string;
  supervisor: string;
  contractual_framework_code: string;
  contractual_framework_type: string;
  contract_type: string;
  ccnl: string;
  hire_date: any;
  termination_date: any;
  schedule_type: string;
  smart_learning_hours: string;
  professional_level: string;
  persona: any[];
  customFields: Record<string, string>;

  uog: UogItem[];

  industry: number | null;
  profession: number | null;
  job: number | null;
  industry_to_be: number | null;
  profession_to_be: number | null;
  job_to_be: number | null;

  staticGroupIds: (string | number)[];
}

export interface UogItem {
  uog: string;
  uog_description: string;
}

export const INITIAL_USER_WIZARD_DATA: UserWizardData = {
  name: "",
  surname: "",
  external_id: "",
  email: "",
  username: "",
  roles: [],
  admin_flag: false,
  external_employee_flag: false,
  new_hire_flag: false,
  manager_flag: false,

  phone_number: "",
  address: "",
  gender: "",
  fiscal_code: "",
  birth_country: "",
  birth_province: "",
  birthday: null,
  privacy_consent_flag: false,
  smart_working_flag: false,
  receieved_credentials_flag: false,
  branch: "",
  language: "",
  preference_language: "",
  ateco_code: "",
  country_code: "",
  country_description: "",
  supervisor: "",
  contractual_framework_code: "",
  contractual_framework_type: "",
  contract_type: "",
  ccnl: "",
  hire_date: null,
  termination_date: null,
  schedule_type: "",
  smart_learning_hours: "",
  professional_level: "PROFESSIONAL",
  persona: [],
  customFields: {},

  uog: [],
  industry: null,
  profession: null,
  job: null,
  industry_to_be: null,
  profession_to_be: null,
  job_to_be: null,

  staticGroupIds: [],
};

const toMillis = (v: any): number | undefined => {
  if (v === null || v === undefined || v === "") return undefined;
  if (typeof v === "number") return v;
  if (typeof v?.valueOf === "function") {
    const ms = v.valueOf();
    if (typeof ms === "number" && !Number.isNaN(ms)) return ms;
  }
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? undefined : t;
};

const extractId = (v: any): number | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return v;
  if (typeof v === "object" && typeof v.id !== "undefined") return Number(v.id);
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const normalizeStringArray = (v: any): string[] => {
  if (!Array.isArray(v)) return [];
  return v
    .map((el) => (typeof el === "string" ? el : el?.value))
    .filter(Boolean);
};

const extractStaticGroupIds = (detail: any): (string | number)[] => {
  const raw =
    detail?.static_group_ids ?? detail?.staticGroupIds ?? detail?.static_groups;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((g: any) => (typeof g === "object" ? (g?.id ?? g?.group_id) : g))
    .filter((v: any) => v !== undefined && v !== null);
};

export const mapDetailToWizard = (detail: any): UserWizardData => {
  if (!detail) return { ...INITIAL_USER_WIZARD_DATA };
  const customFields: Record<string, string> = {};
  CUSTOM_FIELD_KEYS.forEach((k) => {
    if (detail[k] !== undefined && detail[k] !== null)
      customFields[k] = detail[k];
  });
  return {
    ...INITIAL_USER_WIZARD_DATA,
    name: detail.name ?? "",
    surname: detail.surname ?? "",
    external_id: detail.external_id ?? "",
    email: detail.email ?? "",
    username: detail.username ?? "",
    roles: normalizeStringArray(detail.roles),
    admin_flag: !!detail.admin_flag,
    external_employee_flag: !!detail.external_employee_flag,
    new_hire_flag: !!detail.new_hire_flag,
    manager_flag: !!detail.manager_flag,

    phone_number: detail.phone_number ?? "",
    address: detail.address ?? "",
    gender: detail.gender ?? "",
    fiscal_code: detail.fiscal_code ?? "",
    birth_country: detail.birth_country ?? "",
    birth_province: detail.birth_province ?? "",
    birthday: detail.birthday ?? null,
    privacy_consent_flag: !!detail.privacy_consent_flag,
    smart_working_flag: !!detail.smart_working_flag,
    receieved_credentials_flag: !!detail.receieved_credentials_flag,
    branch: detail.branch ?? "",
    language: detail.language ?? "",
    preference_language: detail.preference_language ?? "",
    ateco_code: detail.ateco_code ?? "",
    country_code: detail.country_code ?? "",
    country_description: detail.country_description ?? "",
    supervisor: detail.supervisor ?? "",
    contractual_framework_code: detail.contractual_framework_code ?? "",
    contractual_framework_type: detail.contractual_framework_type ?? "",
    contract_type: detail.contract_type ?? "",
    ccnl: detail.ccnl ?? "",
    hire_date: detail.hire_date ?? null,
    termination_date: detail.termination_date ?? null,
    schedule_type: detail.schedule_type ?? "",
    smart_learning_hours:
      detail.smart_learning_hours !== undefined &&
      detail.smart_learning_hours !== null
        ? String(detail.smart_learning_hours)
        : "",
    professional_level: detail.professional_level || "PROFESSIONAL",
    persona: normalizeStringArray(detail.persona),
    customFields,

    uog: Array.isArray(detail.uog) ? detail.uog : [],
    industry: extractId(detail.industry),
    profession: extractId(detail.profession),
    job: extractId(detail.job),
    industry_to_be: extractId(detail.industry_to_be),
    profession_to_be: extractId(detail.profession_to_be),
    job_to_be: extractId(detail.job_to_be),

    staticGroupIds: extractStaticGroupIds(detail),
  };
};

export const buildUserPayload = (
  data: UserWizardData,
  detail: any,
  isEdit: boolean,
): Record<string, any> => {
  const payload: Record<string, any> = {
    name: data.name || "",
    surname: data.surname || "",
    email: data.email || "",
    username: data.username || "",
    external_id: data.external_id || "",
    phone_number: data.phone_number,
    address: data.address,
    gender: data.gender,
    fiscal_code: data.fiscal_code,
    country_code: data.country_code,
    country_description: data.country_description,
    birthday: toMillis(data.birthday),
    birth_country: data.birth_country,
    birth_province: data.birth_province,
    ateco_code: data.ateco_code || null,
    branch: data.branch,
    external_employee_flag: !!data.external_employee_flag,
    contractual_framework_code: data.contractual_framework_code,
    contractual_framework_type: data.contractual_framework_type,
    new_hire_flag: !!data.new_hire_flag,
    hire_date: toMillis(data.hire_date),
    termination_date: toMillis(data.termination_date),
    contract_type: data.contract_type,
    schedule_type: data.schedule_type,
    ccnl: data.ccnl,
    language: data.language,
    preference_language: data.preference_language || null,
    supervisor: data.supervisor,
    professional_level: data.professional_level,
    privacy_consent_flag: !!data.privacy_consent_flag,
    smart_working_flag: !!data.smart_working_flag,
    teacher_picture: detail?.teacher_picture,
    internal_employee_flag: detail?.internal_employee_flag,
    roles: (data.roles || [])
      .map((r: any) => (typeof r === "string" ? r : r?.value))
      .filter(Boolean),
    smart_learning_hours: data.smart_learning_hours
      ? parseInt(data.smart_learning_hours, 10)
      : null,
    persona: (data.persona || [])
      .map((r: any) => (typeof r === "string" ? r : r?.value))
      .filter(Boolean),
    admin_flag: !!data.admin_flag,
    receieved_credentials_flag: !!data.receieved_credentials_flag,
    manager_flag: !!data.manager_flag,
    uog: data.uog || [],
    job: data.job ?? null,
    industry: data.industry ?? null,
    profession: data.profession ?? null,
    job_to_be: data.job_to_be ?? null,
    industry_to_be: data.industry_to_be ?? null,
    profession_to_be: data.profession_to_be ?? null,
  };

  if (isEdit) {
    payload.level1_proficiency = detail?.level1_proficiency;
    payload.level2_proficiency = detail?.level2_proficiency;
    payload.level3_proficiency = detail?.level3_proficiency;
    payload.level4_proficiency = detail?.level4_proficiency;
  }

  CUSTOM_FIELD_KEYS.forEach((k) => {
    payload[k] = data.customFields?.[k];
  });

  return payload;
};
